/**
 * Dashboard Page
 *
 * Role-aware dashboard that shows different content based on user role:
 * - Platform Admin: Full admin dashboard with platform-wide stats and alerts
 * - Org Admin: Org-level admin features and alerts
 * - Analyst/Investigator: Case management dashboard with summary toggle
 */

import { useTranslation } from 'react-i18next'
import { useAuth, useIsPlatformAdmin, useIsAdmin } from '../hooks'
import { CaseManagementDashboard } from '../components/case-management/CaseManagementDashboard'
import { AlertsSection, CollapsibleSection } from '../components/dashboard'
import { useDashboardData } from './dashboard-page/use-dashboard-data'
import { UsersTile } from './dashboard-page/UsersTile'
import { CasesTile } from './dashboard-page/CasesTile'
import { StorageTile } from './dashboard-page/StorageTile'
import { PlatformTiles } from './dashboard-page/PlatformTiles'
import { RecentUsersTable } from './dashboard-page/RecentUsersTable'

interface HeaderProps {
  displayName: string | undefined
  instanceName: string
}

function DashboardHeader({ displayName, instanceName }: HeaderProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div>
      <h1 className="text-2xl font-semibold" style={{ color: 'var(--text)' }}>
        {t('dashboard.welcomeBack', { name: displayName })}
      </h1>
      <p className="mt-1" style={{ color: 'var(--text-dim)' }}>
        {t('dashboard.happeningIn', { instance: instanceName })}
      </p>
    </div>
  )
}

function LoadingSpinner(): React.ReactElement {
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="db-spinner h-8 w-8 animate-spin rounded-full border-4" />
    </div>
  )
}

interface OverviewProps {
  isPlatformAdmin: boolean
  data: ReturnType<typeof useDashboardData>
}

function OverviewSection({ isPlatformAdmin, data }: OverviewProps): React.ReactElement {
  const { t } = useTranslation('admin')
  const title = isPlatformAdmin
    ? t('dashboard.platformOverview')
    : t('dashboard.organisationOverview')
  return (
    <CollapsibleSection id="platform-overview" title={title}>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <UsersTile
          totalUsers={data.stats.users}
          usersByStatus={data.usersByStatus}
          usersByRole={data.usersByRole}
          recentUsers={data.recentUsers}
          isPlatformAdmin={isPlatformAdmin}
        />
        {!isPlatformAdmin && (
          <>
            <CasesTile summary={data.casesSummary} />
            <StorageTile summary={data.storageSummary} />
          </>
        )}
        {isPlatformAdmin && <PlatformTiles stats={data.stats} />}
      </div>
    </CollapsibleSection>
  )
}

function useInstanceName(orgName: string | null, isPlatformAdmin: boolean): string {
  const { t } = useTranslation('admin')
  if (isPlatformAdmin) return t('dashboard.platformName')
  if (orgName) return orgName
  return t('dashboard.yourOrganisation')
}

export function DashboardPage(): React.ReactElement {
  const { user } = useAuth()
  const isPlatformAdmin = useIsPlatformAdmin()
  const isAdmin = useIsAdmin()
  const data = useDashboardData({ user, isPlatformAdmin, isAdmin })
  const instanceName = useInstanceName(data.orgName, isPlatformAdmin)

  if (data.loading && isAdmin) return <LoadingSpinner />
  if (!isAdmin) return <CaseManagementDashboard />

  return (
    <div className="space-y-8">
      <DashboardHeader
        displayName={user?.displayName ?? user?.username}
        instanceName={instanceName}
      />
      <AlertsSection alerts={data.alerts} loading={data.alertsLoading} />
      <OverviewSection isPlatformAdmin={isPlatformAdmin} data={data} />
      {isPlatformAdmin && <RecentUsersTable users={data.recentUsers} />}
    </div>
  )
}
